import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import Slider from 'react-slick';
import { Hero, HeroMetric } from './Hero';
import { WeeklyHistoryItem } from './WeeklyHistoryItem';
import { useWorkout } from '../hooks/useWorkout';
import { WorkoutUtils } from '../utils/workoutUtils';

// Import slick carousel CSS
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const StatsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userProgress, getWorkoutStats } = useWorkout();
  const stats = getWorkoutStats();
  
  // No longer need state for modal since we're using navigation
  
  // State for carousel position
  const [currentSlide, setCurrentSlide] = useState(() => {
    // Try to restore from localStorage
    const saved = localStorage.getItem('stats-carousel-slide');
    return saved ? parseInt(saved, 10) : 0;
  });
  
  // Get completed weeks data
  const weeklyHistory = WorkoutUtils.getCompletedWeeks(userProgress);

  // Calculate total program progress
  const totalWeeks = 12;
  const completedWeeks = Math.max(0, stats.currentWeek - 1); // Previous completed weeks
  const remainingWeeks = totalWeeks - stats.currentWeek;
  const programProgress = Math.round((stats.currentWeek / totalWeeks) * 100);

  // Calculate workouts remaining - 5 workouts per week (Mon, Tue, Thu, Fri, Sat)
  const totalWorkouts = 12 * 5; // 12 weeks × 5 workouts per week = 60 total workouts
  
  // Calculate how many workouts have been scheduled so far based on current week and day
  const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
  const currentWeek = stats.currentWeek;
  const currentDay = userProgress?.currentDay || 'Monday';
  
  // Workouts from completed weeks
  const workoutsFromCompletedWeeks = Math.max(0, currentWeek - 1) * 5;
  
  // Workouts from current week up to current day (inclusive)
  const currentDayIndex = workoutDays.indexOf(currentDay);
  const workoutsInCurrentWeekSoFar = currentDayIndex >= 0 ? currentDayIndex + 1 : 0;
  
  // Total workouts that should have been scheduled by now
  const scheduledWorkouts = workoutsFromCompletedWeeks + workoutsInCurrentWeekSoFar;
  
  // Remaining workouts = total - scheduled
  const remainingWorkouts = Math.max(0, totalWorkouts - scheduledWorkouts);

  // Custom arrow components
  const CustomPrevArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: 'absolute',
        left: -50,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 2,
        bgcolor: 'background.paper',
        boxShadow: 2,
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 4,
        },
        display: isMobile ? 'none' : 'flex',
      }}
    >
      <ChevronLeft />
    </IconButton>
  );

  const CustomNextArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: 'absolute',
        right: -50,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 2,
        bgcolor: 'background.paper',
        boxShadow: 2,
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 4,
        },
        display: isMobile ? 'none' : 'flex',
      }}
    >
      <ChevronRight />
    </IconButton>
  );

  // Get real workout data
  const overallStats = WorkoutUtils.getOverallStats(userProgress);
  const strengthRecords = WorkoutUtils.getStrengthRecords(userProgress);
  const weekToWeekImprovements = WorkoutUtils.getWeekToWeekImprovements(userProgress);
  const weekToWeekImprovementsByDay = WorkoutUtils.getWeekToWeekImprovementsByDay(userProgress);
  const mostImproved = WorkoutUtils.getMostImprovedExercise(userProgress);

  // Navigation handlers for each stat type
  const handleStatClick = (statType: string, slideIndex: number) => {
    // Save current slide index before navigating
    localStorage.setItem('stats-carousel-slide', slideIndex.toString());
    navigate(`/stat/${statType}`);
  };

  // Hero data configurations with all requested metrics
  const heroConfigs = [
    // 1. Total Program Progress
    {
      title: 'Program Progress',
      subtitle: `You're on Week ${stats.currentWeek} out of 12`,
      metrics: [
        {
          value: `${remainingWorkouts}`,
          label: 'Workouts Remaining',
          size: 'medium' as const,
          align: 'center' as const,
        },
        {
          value: `${programProgress}%`,
          label: 'Total Progress',
          size: 'medium' as const,
          align: 'right' as const,
        },
      ],
      progressValue: programProgress,
      progressLabel: 'Program Completion',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      onClick: () => handleStatClick('program-progress', 0),
      shouldShow: true, // Always show program progress
    },
    
    // 2. Weekly Exercise Improvements Sub-carousel
    {
      title: 'Weekly Improvements',
      subtitle: 'Weight gains compared to last week',
      subCarousel: (() => {
        // If no user progress at all
        if (!userProgress || userProgress.workoutHistory.length === 0) {
          return [{
            value: 'No Data',
            label: 'Start working out to see improvements'
          }];
        }
        
        // If in week 1, no previous week to compare
        if (userProgress.currentWeek === 1) {
          return [{
            value: 'Week 1',
            label: 'Complete this week to see improvements'
          }];
        }
        
        // Create a comprehensive view that includes all workout days
        const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
        const improvementsByDay: { [day: string]: any[] } = {};
        
        // Group existing improvements by day
        weekToWeekImprovementsByDay.forEach(exercise => {
          if (!improvementsByDay[exercise.day]) {
            improvementsByDay[exercise.day] = [];
          }
          improvementsByDay[exercise.day].push({
            value: `+${exercise.weightGain} lbs`,
            label: exercise.exerciseName,
            group: exercise.day
          });
        });
        
        // Create carousel items for all workout days
        const carouselItems: any[] = [];
        
        workoutDays.forEach(day => {
          if (improvementsByDay[day] && improvementsByDay[day].length > 0) {
            // Add all improvements for this day
            carouselItems.push(...improvementsByDay[day]);
          } else {
            // Add "no improvements" item for this day
            carouselItems.push({
              value: 'No Gains',
              label: 'No improvements this week',
              group: day
            });
          }
        });
        
        return carouselItems;
      })(),
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      onClick: () => handleStatClick('weekly-improvements', 1),
      shouldShow: userProgress && userProgress.currentWeek > 1 && weekToWeekImprovementsByDay.length > 0,
    },

    // 3. Most Improved Exercise
    {
      title: 'Most Improved',
      subtitle: 'Biggest strength gains since the start',
      metrics: [
        {
          value: mostImproved.hasData ? mostImproved.exerciseName : 'No Data',
          label: 'Exercise',
          size: 'large' as const,
          align: 'left' as const,
          shrinkText: true, // Enable text shrinking for long exercise names
        },
        {
          value: mostImproved.hasData ? `+${mostImproved.improvement}%` : '0%',
          label: 'Improvement',
          size: 'medium' as const,
          align: 'center' as const,
        },
        {
          value: mostImproved.hasData ? `${mostImproved.startWeight}→${mostImproved.currentWeight}` : '0→0',
          label: 'Weight (lbs)',
          size: 'medium' as const,
          align: 'right' as const,
        },
      ],
      gradient: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
      onClick: () => handleStatClick('most-improved', 2),
      shouldShow: mostImproved.hasData,
    },

    // 4. Highest Weight & Volume Records
    {
      title: 'Personal Records',
      subtitle: 'Your strongest lift and highest volume',
      metrics: [
        {
          value: strengthRecords.highestWeight.hasData ? `${strengthRecords.highestWeight.weight} lbs` : 'No Data',
          label: 'Highest Weight',
          size: 'large' as const,
          align: 'left' as const,
        },
        {
          value: strengthRecords.highestWeight.hasData ? strengthRecords.highestWeight.exerciseName : 'No Data',
          label: 'Exercise',
          size: 'medium' as const,
          align: 'center' as const,
          shrinkText: true, // Enable text shrinking for long exercise names
        },
        {
          value: strengthRecords.highestVolume.hasData 
            ? `${strengthRecords.highestVolume.volume.toLocaleString()} lbs`
            : 'No Data',
          label: 'Highest Volume',
          size: 'large' as const,
          align: 'left' as const,
        },
        {
          value: strengthRecords.highestVolume.hasData ? strengthRecords.highestVolume.exerciseName : 'No Data',
          label: 'Exercise',
          size: 'medium' as const,
          align: 'center' as const,
          shrinkText: true, // Enable text shrinking for long exercise names
        },
      ],
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      onClick: () => handleStatClick('personal-records', 3),
      shouldShow: strengthRecords.highestWeight.hasData || strengthRecords.highestVolume.hasData,
    },

    // 5. Workout Summary
    {
      title: 'Workout Summary',
      subtitle: 'Your complete training overview',
      metrics: [
        {
          value: `${overallStats.totalWorkouts}`,
          label: 'Total Workouts',
          size: 'medium' as const,
          align: 'left' as const,
        },
        {
          value: `${overallStats.totalSets}`,
          label: 'Total Sets',
          size: 'medium' as const,
          align: 'center' as const,
        },
        {
          value: `${overallStats.totalReps.toLocaleString()}`,
          label: 'Total Reps',
          size: 'medium' as const,
          align: 'center' as const,
        },
        {
          value: `${overallStats.totalVolume.toLocaleString()}`,
          label: 'Total Volume',
          size: 'medium' as const,
          align: 'center' as const,
        },
      ],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      onClick: () => handleStatClick('workout-summary', 4),
      shouldShow: overallStats.totalWorkouts > 0,
    },
  ];

  // Filter hero configs to only show items with meaningful data
  const filteredHeroConfigs = heroConfigs.filter(config => config.shouldShow);

  // Update slider settings to handle dynamic number of slides
  const sliderSettings = {
    dots: true,
    infinite: filteredHeroConfigs.length > 1, // Only enable infinite scroll if more than 1 slide
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    swipeToSlide: true,
    touchMove: true,
    prevArrow: filteredHeroConfigs.length > 1 ? <CustomPrevArrow /> : <div />, // Hide arrows if only 1 slide
    nextArrow: filteredHeroConfigs.length > 1 ? <CustomNextArrow /> : <div />,
    dotsClass: 'slick-dots custom-dots',
    initialSlide: Math.min(currentSlide, filteredHeroConfigs.length - 1), // Ensure slide index is valid
    afterChange: (index: number) => {
      setCurrentSlide(index);
      localStorage.setItem('stats-carousel-slide', index.toString());
    },
  };

  // Handler for weekly detail navigation
  const handleWeekClick = (weekNumber: number) => {
    navigate(`/week/${weekNumber}`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 12 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Your Stats
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Track your progress and achievements
        </Typography>
      </Box>

      {/* Hero Carousel */}
      <Box sx={{ 
        position: 'relative',
        '& .custom-dots': {
          bottom: -50,
          '& li button:before': {
            fontSize: 12,
            color: theme.palette.primary.main,
            opacity: 0.5,
          },
          '& li.slick-active button:before': {
            opacity: 1,
            color: theme.palette.primary.main,
          },
        },
      }}>
        <Slider {...sliderSettings}>
          {filteredHeroConfigs.map((config, index) => (
            <Box key={index}>
              <Hero
                title={config.title}
                subtitle={config.subtitle}
                metrics={config.metrics}
                subCarousel={config.subCarousel}
                progressValue={config.progressValue}
                progressLabel={config.progressLabel}
                gradient={config.gradient}
                onClick={config.onClick}
              />
            </Box>
          ))}
        </Slider>
      </Box>

      {/* Instructions */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        {filteredHeroConfigs.length > 1 && (
          <Typography variant="body2" color="text.secondary">
            {isMobile ? 'Swipe left or right' : 'Use arrows or dots'} to view different stats
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Tap on an item to view more details
        </Typography>
      </Box>

      {/* Weekly History Section */}
      <Box sx={{ mt: 8 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
          Weekly History
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          View detailed workout data for completed weeks
        </Typography>
        
        {weeklyHistory.length > 0 ? (
          <Stack spacing={2}>
            {weeklyHistory.map((week) => (
              <WeeklyHistoryItem
                key={week.weekNumber}
                weekNumber={week.weekNumber}
                weekSessions={week.sessions}
                isCompleted={week.isCompleted}
                onClick={() => handleWeekClick(week.weekNumber)}
              />
            ))}
          </Stack>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No workout history yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete some workouts to see your weekly progress here
            </Typography>
          </Box>
        )}
      </Box>

      {/* Weekly detail view is now handled by separate page routing */}
    </Container>
  );
};

export default StatsPage; 